/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useMemo } from 'react';
import { pdfjs } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { ReactSketchCanvas, ReactSketchCanvasRef, ReactSketchCanvasProps } from 'react-sketch-canvas';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { PDFReaderButtons } from '@punchcard/core';
import { useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import GradeModal from './GradeModal';
import { isTeacherApp, parseNumberSeries } from '@punchcard/core/utils/utils';
import PDFViewport from './PDFViewport';
import { useInternalLinks } from '@punchcard/core/hooks';
import { PDFReaderProvider, usePDFReaderContext } from './PDFReaderContext';
import { WIPMode } from '@punchcard/core/constants/Wip';


pdfjs.GlobalWorkerOptions.workerSrc = new URL(
	'pdfjs-dist/build/pdf.worker.min.js',
	import.meta.url,
).toString();

const maxWidth = 800;
interface IProps {
	pdfData: File | null;
	documentLoadSuccess: (numPages: number) => void;
	submitDrawing?: () => void;
	handleCanvasChange?: (path: CanvasPath, index: number) => void;
	currentActivity?: currentActivityAssessment;
	setCurrentActivity: (activity: currentActivityAssessment) => void;
	onStatusChange?: (courseActivityId: number | undefined, status: string, type: string | undefined, gradeReceived: number | undefined) => void;
	skipActivity?: (courseActivityId: number) => void;
	isActivityOpen: boolean;
	setIsDirty?: (isDirty: boolean) => void;
	isPrinting?: boolean;
	setDocumentReady?: () => void;
	selectedPages?: number[];
	setSelectedPages?: React.Dispatch<React.SetStateAction<number[]>>;
	isFinalGrade?: boolean;
	isWorkSampleReview?: boolean;
	isPdfGeneratingView?: boolean;
	handleInternalLinkClick?: (id: number, pageNumber: number) => void;
	isPreparing?: boolean;
	featureFlagInternalLink?: boolean;
	featureFlagLineTool?: boolean;
	featureFlagSymbolSelector?: boolean;
}

const PDFReader = (props: IProps) => {
	return (
		<PDFReaderProvider>
			<PDFReaderInternal {...props} />
		</PDFReaderProvider>
	);
};

const PDFReaderInternal = (props: IProps) => {
	const {
		isPrinting = false, setDocumentReady, selectedPages, setSelectedPages, isFinalGrade, isWorkSampleReview, isPdfGeneratingView,
		handleInternalLinkClick, isPreparing, featureFlagInternalLink, featureFlagLineTool, featureFlagSymbolSelector, setIsDirty
	} = props;
	const [numPages, setNumPages] = React.useState<number | undefined>();
	const renderedPagesRef = React.useRef(0);

	// Separated concerns: business logic, document metadata, and layout measurements
	const {
		activeTextBox, setActiveTextBox,
		gradeModalOpen, setGradeModalOpen,
		setDocumentLoaded,
		mode, setMode: setUpdateMode
	} = usePDFReaderContext();

	const activeTextBoxRef = React.useRef<{ page: number, id: string } | null>(null);
	const methods = useFormContext<WIPForm>();
	const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
	const componentRef = React.useRef<HTMLDivElement>(null);
	const rootStyles = window.getComputedStyle(document.documentElement);
	const colour = rootStyles.getPropertyValue('--school-blue').trim();
	const size = 1;
	const eraserSize = 10;

	const { linkState, handleInternalLinkElement, hideLinkTooltip } = useInternalLinks({
		featureFlagInternalLink
	});

	React.useEffect(() => {	// if the user changes activities, reset the mode so it is not textbox or line.
		setUpdateMode(WIPMode.Hand);
	}, [props.currentActivity]); // eslint-disable-line react-hooks/exhaustive-deps

	const pageNumberArray: number[] = React.useMemo(() => {
		const parsedPages = parseNumberSeries(props.currentActivity?.item?.pageNumbers);
		if (!isPdfGeneratingView || selectedPages === undefined) { return parsedPages; }
		return parsedPages.filter((num: number) => selectedPages.includes(num));
	}, [props.currentActivity?.item?.pageNumbers, isPdfGeneratingView, selectedPages]);

	React.useEffect(() => {
		setTimeout(() => {
			if (activeTextBox && textAreaRef.current) {
				textAreaRef.current.focus();
			}
		}, 50);
	}, [activeTextBox]);
	React.useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			const target = event.target as HTMLElement;
			if (target.closest('.modal.show')) {
				return; // Exit if clicked inside a visible modal
			}
			if (
				componentRef.current &&
				!componentRef.current.contains(event.target as Node)
			) {
				setActiveTextBox(null);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [setActiveTextBox]);

	React.useEffect(() => {
		activeTextBoxRef.current = activeTextBox;
	}, [activeTextBox]);

	React.useEffect(() => {
		if (mode !== WIPMode.Type) {
			setActiveTextBox(null);
		}
	}, [mode, setActiveTextBox]);

	const saveDrawing = (submit: boolean) => {
		if (props.currentActivity) {
			const updatedActivity = {
				...props.currentActivity,
				item: {
					...props.currentActivity.item,
					activityStatus: {
						...props.currentActivity?.item?.activityStatus,
						statusCode: submit ? 'ReadyForReview' : 'InProgress',
					},
				},
			};
			props.setCurrentActivity(updatedActivity);
		}
		props.submitDrawing && props.submitDrawing();
	};

	const options = useMemo(() => {
		return {
			cMapUrl: '/cmaps/',
			standardFontDataUrl: '/standard_fonts/',
		};
	}, []);
	const addHistory = (index: number) => {
		if ((mode === WIPMode.Pencil || mode === WIPMode.Eraser) && props.isActivityOpen) {
			const { setValue, getValues } = methods;
			const { undoHistory, undoIndex } = getValues();
			if (undoIndex !== null && undoIndex !== -1) {
				const removeOldHistory = undoHistory.slice(undoIndex);
				setValue('undoHistory', [index, ...removeOldHistory]); // tihs is in reverse on purpose.
				setValue('undoIndex', null);
			} else {
				setValue('undoHistory', [index, ...undoHistory]); // tihs is in reverse on purpose.
			}
			if (setIsDirty) {
				setIsDirty(true);
			}
		}
	};
	const handleAddToSample = (index: number) => {
		if (setSelectedPages) {
			setSelectedPages((prev: number[]) => {
				if (!prev) { return [index]; }
				return prev.includes(index)
					? prev.filter(i => i !== index) // remove if exists
					: [...prev, index];             // add if not exists
			});
		}
	};
	const onPageRenderSuccess = () => {
		renderedPagesRef.current += 1;
		const allPagesRendered = renderedPagesRef.current === pageNumberArray?.length;
		setDocumentLoaded(allPagesRendered);
		if (setDocumentReady && allPagesRendered) {
			setDocumentReady();
		}
	};

	return (
		<React.Fragment>
			{isPrinting == false && <PDFReaderButtons
				currentActivity={props.currentActivity}
				onStatusChange={props.onStatusChange}
				setGradeModalOpen={setGradeModalOpen}
				gradeModalOpen={gradeModalOpen}
				pdfData={props.pdfData}
				featureFlagLineTool={featureFlagLineTool}
			/>
			}
			<div
				style={{ position: 'relative' }}
				className="d-flex flex-column flex-grow-1 overflow-y-auto no-print"
			>

				{gradeModalOpen &&
					<GradeModal
						setShowGradeModal={setGradeModalOpen}
						currentActivity={props.currentActivity}
						onStatusChange={props.onStatusChange}
					/>}
				<div id="pdf-container" style={{ pageBreakAfter: 'always' }} className={classNames('flex-column flex-grow-1 overflow-y-auto')}
					role="presentation"
					onClick={(e) => {
					// Check if we clicked on an internal link
						if (e.target instanceof HTMLAnchorElement) {
							const anchor = e.target as HTMLAnchorElement;
							if (handleInternalLinkElement(anchor)) {
								e.preventDefault();
								e.stopPropagation();
							}
						}
					}}>
					<PDFViewport
						pdfData={props.pdfData}
						numPages={numPages}
						onDocumentLoadSuccess={onDocumentLoadSuccess}
						options={options}
						pageNumberArray={pageNumberArray}
						pageWidth={maxWidth}
						linkState={linkState}
						handleInternalLinkClick={handleInternalLinkClick}
						hideLinkTooltip={hideLinkTooltip}
						onPageRenderSuccess={onPageRenderSuccess}
						handleInternalLinkElement={handleInternalLinkElement}
						isActivityOpen={props.isActivityOpen}
						size={size}
						colour={colour}
						eraserSize={eraserSize}
						addHistory={addHistory}
						CanvasComponent={Canvas}
						selectedPages={selectedPages}
						handleAddToSample={handleAddToSample}
						isPreparing={isPreparing}
						isFinalGrade={isFinalGrade}
						isWorkSampleReview={isWorkSampleReview}
						currentActivity={props.currentActivity}
						saveDrawing={saveDrawing}
						skipActivity={props.skipActivity}
						setIsDirty={setIsDirty}
						featureFlagSymbolSelector={featureFlagSymbolSelector}
					/>
				</div>
			</div>
		</React.Fragment>
	);
	function onDocumentLoadSuccess({ numPages: nextNumPages }: PDFDocumentProxy): void {
		setNumPages(nextNumPages);
		renderedPagesRef.current = 0;
		props.documentLoadSuccess && props.documentLoadSuccess(nextNumPages);
	}
};

interface CanvasProps extends ReactSketchCanvasProps {
	mode?: WIPModeType;
	index: number;
	isLoading?: boolean;
	statusCode?: string;
	isActivityOpen?: boolean;
	handleAddHistory: (index: number) => void;
}
const CanvasComponent = (props: CanvasProps) => {
	const { mode, index, handleAddHistory, ...otherProps } = props;
	const ref = React.useRef<ReactSketchCanvasRef>(null);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const { watch, getValues } = useFormContext<WIPForm>();
	const [undoIndex, lastAction] = watch(['undoIndex', 'lastAction']);
	const [isDrawing, setIsDrawing] = React.useState(false);
	React.useEffect(() => {
		if (mode) {
			ref.current?.eraseMode(mode === WIPMode.Eraser);
		}
	}, [mode]);

	React.useEffect(() => {	// track mouse up and mouse down events to set isDrawing state
		const canvasEl = containerRef.current;
		if (!canvasEl) { return; }

		const handlePointerDown = () => setIsDrawing(true);
		const handlePointerUp = () => {
			if (isDrawing) {
				setIsDrawing(false);
				handleAddHistory(index); // fire on mouse up only
			}
		};
		canvasEl.addEventListener('pointerdown', handlePointerDown);
		canvasEl.addEventListener('pointerup', handlePointerUp);

		return () => {
			canvasEl.removeEventListener('pointerdown', handlePointerDown);
			canvasEl.removeEventListener('pointerup', handlePointerUp);
		};
	}, [isDrawing, index, handleAddHistory]);

	React.useEffect(() => {
		const { undoHistory } = getValues();
		if (undoHistory) {
			if (undoIndex !== null && (undoHistory[undoIndex] === index) && lastAction === 'undo') {
				ref.current?.undo();
			}
			if (undoIndex !== null && (undoHistory[undoIndex + 1] === index) && lastAction === 'redo') {
				ref.current?.redo();
			}
		}
	}, [undoIndex, lastAction, getValues, index]);
	return (
		<div ref={containerRef}>
			<ReactSketchCanvas
				ref={ref}
				readOnly={props.isLoading || props.readOnly || props.mode === WIPMode.Line
					|| props.mode === WIPMode.Type || props.mode === WIPMode.Hand
					|| !props.isActivityOpen || props.statusCode === 'ReadyForReview' || isTeacherApp}
				{...otherProps}
			/>
		</div>
	);
};
const Canvas = React.memo(CanvasComponent, (prev, next) => {
	//return prev.defaultValue === next.defaultValue && next.isLoading === true;
	return next.isLoading === true;
});



export default PDFReader;
